import { useState } from "react"
import { Stage, StatePill } from "./Stage"
import { Button } from "@/components/ui/button"
import { Receipt, ArrowUpRight, Loader2, PlayCircle } from "lucide-react"
import { APCMState } from "../../types/apcm-ops.types"
import { Link } from "react-router-dom"
import * as api from "../../api/apcm-ops.api"
import { useToast } from "@/components/ui/use-toast"
import { timestampMs } from "@/lib/datetime"

interface Props {
  orgId: string
  stateCounts: Record<string, number>
  lastBackfillRanAt: string | null
  lastBackfillMonth: string | null
  onRefresh: () => void
}

function timeAgo(iso: string | null): string {
  if (!iso) return "never"
  const then = timestampMs(iso)
  if (Number.isNaN(then)) return "unknown"
  const diffMin = (Date.now() - then) / 60000
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${Math.round(diffMin)}m ago`
  const diffH = diffMin / 60
  if (diffH < 24) return `${Math.round(diffH)}h ago`
  return `${Math.round(diffH / 24)}d ago`
}

function humanMonth(iso: string | null): string | null {
  if (!iso) return null
  const [y, m] = iso.split("-").map((s) => parseInt(s, 10))
  if (!y || !m) return null
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
  })
}

export function BillingStage({
  orgId,
  stateCounts,
  lastBackfillRanAt,
  lastBackfillMonth,
  onRefresh,
}: Props) {
  const completed = stateCounts[APCMState.BILLING_COMPLETED] || 0
  const { toast } = useToast()
  const [running, setRunning] = useState(false)

  const handleManualRun = async () => {
    setRunning(true)
    try {
      const r = await api.backfillBillingClassifications(orgId)
      toast({
        title: "Backfill complete",
        description:
          `Created ${r.rows_created || 0} row${r.rows_created === 1 ? "" : "s"}, ` +
          `skipped ${r.rows_skipped_existing || 0} existing.`,
      })
      onRefresh()
    } catch (err: any) {
      toast({
        title: "Backfill failed",
        description: err?.response?.data?.detail || err?.message || "Unknown error",
        variant: "destructive",
      })
    } finally {
      setRunning(false)
    }
  }

  const ranAgo = timeAgo(lastBackfillRanAt)
  const latestMonth = humanMonth(lastBackfillMonth)

  return (
    <Stage
      number={5}
      accent="billing"
      icon={Receipt}
      title="Billing"
      description="G-code classifications (G-0556 / G-0557 / G-0558) auto-populated nightly by the backfill job."
      total={completed}
      showConnector={false}
    >
      <div className="rounded-xl border bg-card p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">Billing codes page</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            View and export the monthly G-code CSV. The nightly job fills in missing months from
            consent → last-complete month, capped at disenrollment.
          </p>
        </div>
        <Link
          to="/billing-codes"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border bg-card px-3 py-2 text-xs font-medium hover:bg-accent"
        >
          Open billing page
          <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="rounded-xl border bg-muted/20 px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Last auto-run
          </p>
          <p className="text-sm mt-0.5">
            {lastBackfillRanAt ? (
              <>
                <span className="font-medium">{ranAgo}</span>
                {latestMonth && (
                  <span className="text-muted-foreground">
                    {" "}· latest month: {latestMonth}
                  </span>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">Never run yet</span>
            )}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={running}
          onClick={handleManualRun}
          className="shrink-0 h-8 gap-1.5"
        >
          {running ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <PlayCircle className="h-3 w-3" />
          )}
          Run now
        </Button>
      </div>

      {completed > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <StatePill label="BILLING_COMPLETED" count={completed} tone="emerald" />
        </div>
      )}
    </Stage>
  )
}
